import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';

import { CONTENT_KEYS } from '@core/models/content-keys';
import { BaseActionHandlerService } from '@core/services/action-handler/base-action-handler.service';
import { ContentService } from '@core/services/content.service';
import { JexlService } from '@core/services/jexl.service';
import { LanguageService } from '@core/services/language.service';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import { type DynamicAction } from '@shared/registry';

import { type PaymentCardConfig, type PaymentCardContent } from './payment-card.types';
import { WalletService } from './wallet.service';

@Component({
  selector: 'app-payment-card',
  template: `
    <app-groups-renderer
      [groups]="interpolatedGroups()"
      [loadingGroups]="loadingGroups()"
      [isLoading]="isLoading()"
      (componentAction)="onAction($event)"
    />
  `,
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService, WalletService],
})
export class PaymentCard {
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly walletService = inject(WalletService);
  private readonly contentService = inject(ContentService<PaymentCardContent>);
  private readonly languageService = inject(LanguageService);
  private readonly jexlService = inject(JexlService);

  readonly config = input.required<PaymentCardConfig>();

  protected readonly state = this.walletService.state;
  protected readonly wallet = this.walletService.wallet;
  protected readonly subscription = this.walletService.subscription;

  private readonly actionHandler = this.baseActionHandler.createScope({
    updatePaymentMethod: () => this.walletService.updatePaymentMethod(),
  });

  protected readonly isLoading = computed(() => {
    const walletState = this.state();
    const contentState = this.contentService.state();
    return walletState.isLoading() || walletState.isInitial() || contentState.isLoading() || contentState.isInitial();
  });

  private readonly rawContent = this.contentService.rawContent;

  /**
   * Base context with wallet data - used for interpolating visitCardConfig.
   * Does NOT include visitCardConfig itself to avoid circular reference.
   * Returns null when subscription hasn't loaded yet.
   */
  private readonly baseJexlContext = computed<Record<string, unknown> | null>(() => {
    const config = this.config();
    const wallet = this.wallet();
    const subscription = this.subscription();
    if (!subscription) return null;

    return {
      // Raw data
      subscription,
      wallet,

      // Computed flags
      hasWallet: !!wallet,
      hasError: !!subscription.error,
      isHighlighted: config.isHighlighted ?? true,

      // Config passthrough
      border: config.border,
      chip: config.chip,
      canDismiss: config.canDismiss,
      dismissAction: config.dismissAction,

      // Optional text overrides
      noWalletText: config.noWalletText,
      paymentFailedText: config.paymentFailedText,

      // Optional behavior flags
      showManageButton: config.showManageButton ?? true,
      manageRoute: config.manageRoute,
    };
  });

  /**
   * Full JEXL context including interpolated visitCardConfig.
   * If visitCardConfig was passed with JEXL expressions (e.g., from parent with _skipInterpolation),
   * those expressions are now evaluated with wallet/subscription data available.
   */
  private readonly jexlContext = computed<Record<string, unknown>>(() => {
    const baseContext = this.baseJexlContext();
    const config = this.config();

    // Only process visitCardConfig when we have subscription context.
    // When baseContext is null (subscription not loaded yet), JEXL expressions
    // will fail and leave strings instead of evaluated arrays, breaking VisitCard.
    const visitCardConfig =
      baseContext && config.visitCardConfig ? this.jexlService.process(config.visitCardConfig, baseContext) : undefined;

    return {
      ...baseContext,
      visitCardConfig,
    };
  });

  private static readonly defaultLoadingGroups: ComponentGroup[] = [
    {
      id: 'skeleton-wrapper',
      layout: {
        direction: 'column',
        gap: 'md',
        className:
          'px-(--visitcard-padding-x) py-(--visitcard-padding-y) rounded-(--visitcard-border-radius) border border-(--p-surface-200)',
      },
      items: [
        {
          id: 'sk-chip',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '24px', width: '80px', borderRadius: 'full' } },
        },
        {
          id: 'sk-text1',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '28px', width: '50%' } },
        },
        {
          id: 'sk-text2',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '28px', width: '70%' } },
        },
        {
          id: 'sk-btn',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '44px', borderRadius: 'full' } },
        },
      ],
    } as ComponentGroup,
  ];

  protected readonly loadingGroups = computed<ComponentGroup[]>(() => {
    const customGroups = this.config().loadingGroups;
    return customGroups ?? PaymentCard.defaultLoadingGroups;
  });

  protected readonly interpolatedGroups = computed<ComponentGroup[]>(() => {
    const content = this.rawContent();
    if (!content?.groups) return [];
    return this.contentService.interpolateGroups(content.groups, this.jexlContext());
  });

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      untracked(() => {
        this.contentService
          .loadFeatureContent(CONTENT_KEYS.COMPONENTS._id, CONTENT_KEYS.COMPONENTS.PAYMENT_CARD)
          .catch(console.error);
      });
    });

    afterNextRender(() => {
      this.walletService.loadWallet(this.config().subscription).catch(console.error);
    });
  }

  protected onAction(event: DynamicAction): void {
    void this.actionHandler.execute(event);
  }
}
